"use client";

/**
 * Landing side of the forced-sign-out flow.
 *
 * A session killed by a dead refresh token redirects here with the interrupted
 * route stashed (`lib/authReturnRoute`). This hook opens the app-wide auth
 * modal on arrival and, once the user authenticates, sends them back to where
 * they were.
 *
 * Ratified shape (`planning/auth-session-expiry/plan.md` D1): the sign-in
 * prompt belongs on the home page, never over the unauthorized screen the user
 * was thrown out of.
 *
 * It knows about routes and auth state and nothing else. WHAT was interrupted
 * is the caller's to name: it hands in `releaseParkedIntents`, so this hook
 * never has to enumerate a feature's parked state to abandon it correctly.
 */

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import type { OpenAuthModalOptions } from "@/contexts/layout";
import {
  clearAuthReturnRoute,
  consumeAuthReturnRoute,
  markAuthReturnRestore,
  peekAuthReturnRoute,
} from "@/lib/authReturnRoute";

interface AuthReturnRouteResumeInputLocal {
  authLoading: boolean;
  isAuthenticated: boolean;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
  /**
   * Release whatever the caller parked against this interruption, beyond the
   * route itself. The route is only half of what an interrupted action leaves
   * behind — the other half is the action it was interrupted mid-way through,
   * and the caller is the only layer that knows what that is.
   *
   * It has to be released HERE, not by whatever parked it: this gate is opened
   * on landings where the surface that would notice the dismissal is not
   * mounted at all (`/?auth=error` is the plain case), so its own release
   * effect never runs and the parked half would sit there until its TTL.
   */
  releaseParkedIntents: () => void;
}

export function useAuthReturnRouteResume(
  input: AuthReturnRouteResumeInputLocal,
): void {
  const { authLoading, isAuthenticated, openAuthModal, releaseParkedIntents } =
    input;
  const router = useRouter();
  const hasOpenedGate = useRef(false);

  /**
   * Closing the gate abandons the resume: the stashed route describes a live
   * interruption, not a bookmark to replay on some later visit. Park and
   * release move together, so the intents parked against it go with it —
   * a surviving intent would resurrect the abandoned action the next time the
   * user opens the same surface inside its TTL.
   */
  const abandonInterruptedRoute = useCallback((): void => {
    releaseParkedIntents();
    clearAuthReturnRoute();
  }, [releaseParkedIntents]);

  useEffect(() => {
    // Auth state resolves after mount; acting early would either open the gate
    // for an already-signed-in user or redirect before the session lands.
    if (authLoading) return;
    if (!peekAuthReturnRoute()) return;

    if (isAuthenticated) {
      const route = consumeAuthReturnRoute();
      if (route) {
        // Arm the restore marker first, scoped to this route: it may have been
        // deleted or had access revoked while the user was signed out, and D1
        // requires that to land them home rather than on `/forbidden` or the
        // not-found page.
        markAuthReturnRestore(route);
        router.replace(route);
      }
      return;
    }

    // The gate is opened once per landing. Re-opening on every render pass
    // would also re-arm `onDismiss` against a modal the user already closed.
    if (hasOpenedGate.current) return;
    hasOpenedGate.current = true;
    openAuthModal({ onDismiss: abandonInterruptedRoute });
  }, [
    abandonInterruptedRoute,
    authLoading,
    isAuthenticated,
    openAuthModal,
    router,
  ]);
}
